import axios from 'axios';
import { AUTH_HOST } from './csqConstants';
import { QUALITY_SERVICE_API_URL } from '../../config';
import oktaAuth from '../../auth/oktaAuth';

/*
 * Logs in to the ContentSquare auth service so the Demo Environment
 * Management page can authenticate with email + password instead of
 * requiring a manually pasted bearer token.
 *
 * Mirrors the request used by sales-demo-env-setup's `src/lib/auth.js` /
 * the app's own login flow:
 *   POST https://auth.csq.io/v2/auth/login?responseType=accessToken
 *   body: { email, password }
 *
 * auth.csq.io doesn't send CORS headers allowing the quality-hub origin, so
 * this call can't be made directly from the browser in any environment.
 * Instead it's routed through Quality Service's `/hub/csq-proxy` passthrough
 * (see app-quality-dashboard's `src/hub/csq-proxy/csq-proxy.controller.ts`),
 * which forwards it server-to-server, where CORS doesn't apply. That proxy
 * endpoint requires the same Hub auth (Okta bearer token) as every other
 * /hub/* endpoint.
 */

// A dedicated axios instance — the Hub auth header is added explicitly per
// request below instead of relying on api.js's interceptor.
const authClient = axios.create();

/**
 * Extracts the access token from the login response, tolerating the common
 * envelope shapes ({ accessToken }, { access_token }, { token }, { payload: {...} }).
 */
function extractToken(data) {
  if (!data) return undefined;
  if (typeof data === 'string') return data;
  const source = data.payload && typeof data.payload === 'object' ? data.payload : data;
  return source.accessToken || source.access_token || source.token;
}

/**
 * Logs in and returns a bearer access token for webapi.contentsquare.com.
 *
 * @param {object} params
 * @param {string} params.email
 * @param {string} params.password
 * @param {string} [params.host] - Auth host, defaults to auth.csq.io.
 * @returns {Promise<string>} The bearer access token.
 */
export async function loginToContentSquare({ email, password, host = AUTH_HOST }) {
  if (!email || !password) {
    throw new Error('Login requires both email and password.');
  }

  const oktaToken = await oktaAuth.getOrRenewAccessToken();
  const headers = { Accept: 'application/json, text/plain, */*', 'Content-Type': 'application/json' };
  if (oktaToken) {
    headers['Authorization'] = `Bearer ${oktaToken}`;
  }

  let response;
  try {
    response = await authClient.post(
      `${QUALITY_SERVICE_API_URL}/hub/csq-proxy/auth/v2/auth/login?responseType=accessToken`,
      { email, password },
      { headers },
    );
  } catch (err) {
    if (err.response) {
      const body = typeof err.response.data === 'string' ? err.response.data : JSON.stringify(err.response.data);
      throw new Error(`Login failed (HTTP ${err.response.status}) for ${host}: ${body || '<empty>'}`);
    }
    throw new Error(
      `Login request to ${host} (via the Quality Service proxy) failed with no HTTP response. ` +
        `Original error: ${err.message}`,
    );
  }

  const token = extractToken(response.data);
  if (!token) {
    throw new Error(`Login succeeded but no access token was found in the response: ${JSON.stringify(response.data)}`);
  }
  return token;
}
